import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Star, Gift, TrendingUp, Loader2, AlertCircle } from "lucide-react";
import { apiUrl } from "@/lib/api";

export default function PublicLoyaltyCard() {
  const { tenantId, customerId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    fetch(apiUrl(`/public/loyalty/${tenantId}/${customerId}`))
      .then(async (r) => {
        if (!r.ok) {
          const t = await r.text();
          throw new Error(t || `Errore ${r.status}`);
        }
        return r.json();
      })
      .then(setData)
      .catch((e) => setErr(e.message || "Errore caricamento"));
  }, [tenantId, customerId]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F1A3D] to-[#1E2D5B] text-white px-4">
        <div className="bg-white/10 backdrop-blur rounded-2xl p-8 max-w-md text-center" data-testid="public-loy-error">
          <AlertCircle className="w-12 h-12 mx-auto mb-3 text-amber-300" />
          <h1 className="text-xl font-bold mb-2">Cliente non trovato</h1>
          <p className="text-white/70 text-sm">Il QR code potrebbe essere scaduto o non valido.</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F1A3D] to-[#1E2D5B]">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  const progressPct = data.threshold > 0
    ? Math.min(100, ((data.points % data.threshold) / data.threshold) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F1A3D] via-[#1E2D5B] to-[#2A3D7B] text-white px-4 py-8">
      <div className="max-w-md mx-auto" data-testid="public-loy-card">
        {/* Header */}
        <div className="text-center mb-6">
          {data.logo_url && <img src={data.logo_url} alt="logo" className="mx-auto mb-3 max-h-16 object-contain bg-white/10 p-2 rounded-lg" />}
          <h1 className="font-heading text-2xl font-bold tracking-tight">{data.business_name}</h1>
          <div className="text-xs uppercase tracking-[0.3em] text-white/50 mt-1">Carta Fedeltà</div>
        </div>

        {/* Points hero card */}
        <div className="bg-gradient-to-br from-amber-400 to-amber-600 text-[#1E2D5B] rounded-2xl p-6 shadow-2xl mb-5 relative overflow-hidden">
          <div className="absolute -right-6 -top-6 opacity-10">
            <Star className="w-32 h-32 fill-current" />
          </div>
          <div className="text-xs uppercase tracking-[0.25em] font-bold mb-1">{data.customer_name}</div>
          <div className="flex items-baseline gap-2 mb-3">
            <div className="font-mono font-black text-6xl tracking-tighter" data-testid="public-loy-points">{data.points}</div>
            <div className="font-mono text-lg font-bold uppercase">punti</div>
          </div>
          <div className="text-xs font-medium mb-2">
            {data.points_to_next_reward > 0
              ? <>Ti mancano <strong>{data.points_to_next_reward} pt</strong> al prossimo premio</>
              : <>Premio sbloccato! 🎁</>}
          </div>
          <div className="bg-[#1E2D5B]/20 rounded-full h-2 overflow-hidden">
            <div className="bg-[#1E2D5B] h-full rounded-full transition-all" style={{ width: `${progressPct}%` }} />
          </div>
        </div>

        {/* Available discount */}
        {data.available_units > 0 && (
          <div className="bg-[#2F9E77]/15 border border-[#2F9E77]/40 rounded-2xl p-5 mb-5" data-testid="public-loy-available">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#2F9E77] flex items-center justify-center shrink-0">
                <Gift className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest text-[#2F9E77] font-bold">Sconto disponibile</div>
                <div className="font-mono font-black text-3xl text-[#2F9E77]">{data.available_discount.toFixed(2)}€</div>
                <div className="text-xs text-white/60">Mostra questa schermata alla cassa</div>
              </div>
            </div>
          </div>
        )}

        {/* Conversion rate info */}
        <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-4 mb-5">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-white/60" />
            <div className="text-xs uppercase tracking-widest text-white/60 font-mono">Come funziona</div>
          </div>
          <div className="text-sm space-y-1 font-mono">
            <div>· Ogni 1€ speso = <strong>{data.points_per_euro} pt</strong></div>
            <div>· {data.threshold} pt = <strong>{data.value_per_threshold}€</strong> di sconto</div>
            <div>· Visite totali: <strong>{data.visits}</strong></div>
          </div>
        </div>

        {/* Recent activity */}
        {data.recent_activity?.length > 0 && (
          <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-4">
            <div className="text-xs uppercase tracking-widest text-white/60 font-mono mb-3">Ultima attività</div>
            <div className="space-y-2">
              {(Array.isArray(data.recent_activity) ? data.recent_activity : []).map((a) => (
                <div key={a.id} className="flex items-center justify-between text-xs" data-testid={`public-loy-act-${a.id}`}>
                  <div className="flex-1 min-w-0 mr-2">
                    <div className="text-white/90 truncate">{a.reason}</div>
                    <div className="text-white/40 text-[10px]">{new Date(a.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                  <div className={`font-mono font-bold shrink-0 ${a.delta_points > 0 ? "text-[#2F9E77]" : "text-amber-300"}`}>
                    {a.delta_points > 0 ? "+" : ""}{a.delta_points}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="text-center text-[10px] text-white/30 mt-6 font-mono">
          Powered by VELTA SISTEMI
        </div>
      </div>
    </div>
  );
}
